import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import LoadStep from './LoadStep';
import SupplierStep from './SupplierStep';
import SummaryDetails from '../components/wizard/SummaryDetails';
import SummaryStep from './SummaryStep';
import ConfirmationStep from './ConfirmationStep';
import { useCreateDraftLoadMutation } from '../loadsEndpoints';
import { initialLoadState, initialSupplierState } from '../components/wizard/initialState';
import transformLoad from '../components/wizard/transformLoad';

const initialState = {
  ...initialLoadState,
  ...initialSupplierState,
};

const pickLoadState = R.pick([
  'deliveryEndDateTime',
  'deliveryStartDateTime',
  'driver',
  'location',
  'poNumber',
  'trailer',
]);

const CreateLoadWizard = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [loadState, setLoadState] = useState(initialState);
  const [draftDialogOpened, setDraftDialogOpened] = useState(false);

  const [submit] = useCreateDraftLoadMutation();

  const next = () => {
    setStep(R.inc);
  };
  const prev = () => {
    setStep(R.dec);
  };
  const reset = () => {
    setStep(1);
    setLoadState(({ driver, location, trailer, compartments }) => ({
      ...initialState,
      ...initialSupplierState,
      compartments,
      driver,
      location,
      trailer,
    }));
  };

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick') return;
    const zipped = R.zipWith(
      R.equals,
      R.values(pickLoadState(initialLoadState)),
      R.values(pickLoadState(loadState))
    );

    const step1Completed = R.all(R.equals(false), zipped);

    if (!step1Completed) {
      setDraftDialogOpened(true);
    } else {
      onClose();
    }
  };

  const handleSaveDraft = () => {
    const load = transformLoad(loadState);
    submit({ load }).then(onClose);
  };

  const stepProps = {
    loadState,
    next,
    onClose: handleClose,
    prev,
    reset,
    setLoadState,
  };

  return (
    <Drawer anchor="right" onClose={handleClose} open>
      <Box height={1} width={390}>
        {step === 1 && <LoadStep {...stepProps} />}
        {step === 2 && <SupplierStep {...stepProps} />}
        {step === 3 && (
          <SummaryStep {...stepProps}>
            <SummaryDetails loadState={loadState} />
          </SummaryStep>
        )}
        {step === 4 && (
          <ConfirmationStep {...stepProps}>
            <SummaryDetails loadState={loadState} />
          </ConfirmationStep>
        )}
      </Box>
      {draftDialogOpened && (
        <Dialog
          maxWidth="xs"
          open
          slotProps={{
            paper: { elevation: 10 },
          }}
          sx={{ '& .MuiDialog-paper': { height: 200, width: 380 } }}
        >
          <DialogTitle>Would you like to save this load as a draft?</DialogTitle>
          <DialogContent>
            If you Delete the load, you will not be able to finish it later.
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={onClose} variant="outlined">
              Delete
            </Button>
            <Button onClick={handleSaveDraft}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </Drawer>
  );
};

CreateLoadWizard.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default CreateLoadWizard;
